import {
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileText,
  IndianRupee,
  Receipt,
  Search,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import API from '../../lib/api';

const BRAND_LABELS = {
  myca:      { label: 'myCA',      color: 'bg-[#6C63FF]/15 text-[#6C63FF]' },
  filemytax: { label: 'FileMyTax', color: 'bg-blue-100 text-blue-700' },
  unknown:   { label: '—',         color: 'bg-gray-100 text-gray-600' },
};

const STATUS_LABELS = {
  paid:     { label: 'Paid',     color: 'bg-emerald-100 text-emerald-700' },
  refunded: { label: 'Refunded', color: 'bg-orange-100 text-orange-700' },
  void:     { label: 'Void',     color: 'bg-red-100 text-red-600' },
};

const formatMoney = (amount, currency = 'INR') => {
  if (amount == null) return '–';
  const n = Number(amount);
  if (Number.isNaN(n)) return '–';
  const sym = currency === 'INR' ? '₹' : currency + ' ';
  return `${sym}${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

export default function InvoicesPanel() {
  const [filters, setFilters] = useState({ search: '', brand: '', status: '' });
  const [data, setData]       = useState({ invoices: [], total: 0, counts: {}, page: 1, pages: 1 });
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [previewingId, setPreviewingId]   = useState(null);
  const [preview, setPreview]             = useState(null); // { invoice, url }
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 20 };
      if (filters.search) params.search = filters.search;
      if (filters.brand)  params.brand  = filters.brand;
      if (filters.status) params.status = filters.status;
      const { data: res } = await API.get('/admin/invoices', { params });
      setData(res);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load invoices');
    }
    setLoading(false);
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const handleFilter = (key, val) => { setFilters((f) => ({ ...f, [key]: val })); setPage(1); };
  const handleReset  = () => { setFilters({ search: '', brand: '', status: '' }); setPage(1); };

  const handleDownload = async (invoice) => {
    setDownloadingId(invoice.id);
    try {
      const res = await API.get(`/admin/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a   = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${String(invoice.invoice_number).replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to download invoice PDF');
    }
    setDownloadingId(null);
  };

  const handlePreview = async (invoice) => {
    setPreviewingId(invoice.id);
    try {
      // Fetch the PDF as an authenticated blob (axios carries the admin cookie
      // via withCredentials), then point an iframe at the object URL. A plain
      // <a target=_blank> wouldn't carry the cookie reliably across origins.
      const res = await API.get(`/admin/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPreview({ invoice, url });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to preview invoice');
    }
    setPreviewingId(null);
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  // Close on Esc for keyboard users
  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closePreview(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview]);

  const handleExport = async () => {
    setExporting(true);
    try {
      // Page through every matching invoice (server caps `limit` at 100).
      const baseParams = {};
      if (filters.search) baseParams.search = filters.search;
      if (filters.brand)  baseParams.brand  = filters.brand;
      if (filters.status) baseParams.status = filters.status;

      const all = [];
      let p = 1;
      while (true) {
        const { data: res } = await API.get('/admin/invoices', {
          params: { ...baseParams, page: p, limit: 100 },
        });
        all.push(...(res.invoices || []));
        if (p >= (res.pages || 1) || (res.invoices || []).length === 0) break;
        p += 1;
      }

      const headers = [
        'Invoice No.', 'Issued At', 'Brand', 'Status',
        'Buyer Name', 'Buyer Email', 'Buyer Phone', 'City', 'State',
        'Service', 'Payment Method', 'Payment ID', 'Order ID',
        'Base Amount', 'GST Amount', 'Total Amount', 'Currency',
      ];

      // RFC 4180 quoting.
      const esc = (v) => {
        if (v == null) return '';
        const s = String(v);
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const rows = all.map((i) => [
        i.invoice_number,
        i.issued_at ? new Date(i.issued_at).toLocaleString('en-IN') : '',
        BRAND_LABELS[i.brand || 'unknown']?.label || i.brand,
        STATUS_LABELS[i.status]?.label || i.status,
        i.buyer_name, i.buyer_email, i.buyer_phone, i.buyer_city, i.buyer_state,
        i.service_name, i.payment_method, i.payment_id,
        i.myca_order_id ? `#${i.myca_order_id}` : '',
        i.base_amount, i.gst_amount, i.total_amount, i.currency,
      ].map(esc).join(','));

      const csv = [headers.map(esc).join(','), ...rows].join('\r\n');
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `invoices-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert(err.response?.data?.detail || 'Failed to export invoices');
    }
    setExporting(false);
  };

  const counts = data.counts || {};
  const paid     = counts.by_status?.paid     || { count: 0, amount: 0 };
  const refunded = counts.by_status?.refunded || { count: 0, amount: 0 };
  const grand    = counts.grand_total || 0;

  return (
    <div className="space-y-5">
      {/* Hero header — kept consistent with the rest of the panel */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#334155] text-white px-6 py-6">
        <div className="absolute -top-20 -right-12 w-60 h-60 bg-[#A78BFA]/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-12 w-60 h-60 bg-[#22D3EE]/15 rounded-full blur-3xl" />
        <div className="relative flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-sm border border-white/15 flex items-center justify-center">
              <Receipt className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-heading font-bold leading-tight">
                Invoices
              </h1>
              <p className="text-sm text-white/75 font-body mt-0.5">
                {data.total} {data.total === 1 ? 'invoice' : 'invoices'} · GST-inclusive PDFs for every captured payment
              </p>
            </div>
          </div>

          {/* Hero stat pills */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2.5 px-3.5 py-2 rounded-2xl bg-white/[0.1] backdrop-blur-sm border border-white/15">
              <IndianRupee className="h-4 w-4 text-emerald-300 flex-shrink-0" />
              <div className="leading-tight">
                <p className="text-lg font-heading font-bold text-white">{formatMoney(grand)}</p>
                <p className="text-[10px] font-body uppercase tracking-wide text-white/60 -mt-0.5">Total Billed</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5 px-3.5 py-2 rounded-2xl bg-white/[0.1] backdrop-blur-sm border border-white/15">
              <FileText className="h-4 w-4 text-white/80 flex-shrink-0" />
              <div className="leading-tight">
                <p className="text-lg font-heading font-bold text-white">{paid.count}</p>
                <p className="text-[10px] font-body uppercase tracking-wide text-white/60 -mt-0.5">Paid</p>
              </div>
            </div>
            {refunded.count > 0 && (
              <div className="flex items-center gap-2.5 px-3.5 py-2 rounded-2xl bg-white/[0.1] backdrop-blur-sm border border-white/15">
                <FileText className="h-4 w-4 text-orange-300 flex-shrink-0" />
                <div className="leading-tight">
                  <p className="text-lg font-heading font-bold text-white">{refunded.count}</p>
                  <p className="text-[10px] font-body uppercase tracking-wide text-white/60 -mt-0.5">Refunded</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter bar — white background as requested */}
      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9CA3AF]" />
          <input
            type="text"
            placeholder="Search invoice #, buyer name, email, payment ID, order ID…"
            value={filters.search}
            onChange={(e) => handleFilter('search', e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm font-body border border-[#E8E6F8] rounded-lg bg-white text-[#1A1A2E] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
          />
        </div>
        <select
          value={filters.brand}
          onChange={(e) => handleFilter('brand', e.target.value)}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-white text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        >
          <option value="">All Brands</option>
          <option value="myca">myCA</option>
          <option value="filemytax">FileMyTax</option>
        </select>
        <select
          value={filters.status}
          onChange={(e) => handleFilter('status', e.target.value)}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-white text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        >
          <option value="">All Statuses</option>
          <option value="paid">Paid</option>
          <option value="refunded">Refunded</option>
          <option value="void">Void</option>
        </select>
        <button
          onClick={handleExport}
          disabled={exporting || data.total === 0}
          title="Download all matching invoices as CSV"
          className="flex items-center gap-1.5 text-xs font-body font-medium text-white bg-[#6C63FF] hover:bg-[#5B52CC] disabled:opacity-60 disabled:cursor-not-allowed px-3 py-2 rounded-lg transition-colors"
        >
          {exporting ? (
            <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
        <button
          onClick={handleReset}
          className="text-xs font-body text-[#6B7280] hover:text-[#1A1A2E] px-3 py-2 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] transition-colors"
        >
          Reset
        </button>
      </div>

      {/* Table card — white background */}
      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-sm text-red-500 font-body">{error}</p>
            <button onClick={load} className="mt-2 text-xs text-[#6C63FF] underline font-body">Retry</button>
          </div>
        ) : data.invoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Receipt className="h-8 w-8 text-[#9CA3AF] mb-2" />
            <p className="text-sm text-[#6B7280] font-body">No invoices match these filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="bg-[#F5F4FB] border-b border-[#E8E6F8]">
                  <th className="px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">Invoice</th>
                  <th className="px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">Buyer</th>
                  <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">Service</th>
                  <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">Brand</th>
                  <th className="px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">Total</th>
                  <th className="hidden xl:table-cell px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">Issued</th>
                  <th className="px-4 py-3 text-right text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E6F8]">
                {data.invoices.map((inv) => {
                  const brandMeta  = BRAND_LABELS[inv.brand || 'unknown'] || BRAND_LABELS.unknown;
                  const statusMeta = STATUS_LABELS[inv.status] || { label: inv.status, color: 'bg-gray-100 text-gray-600' };
                  return (
                    <tr key={inv.id} className="hover:bg-[#F5F4FB]/60 transition-colors">
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <p className="font-mono text-xs text-[#1A1A2E] font-medium">{inv.invoice_number}</p>
                        <p className="text-[10px] text-[#9CA3AF] mt-0.5">
                          {inv.payment_method || '—'}
                          {inv.myca_order_id && <> · Order #{inv.myca_order_id}</>}
                        </p>
                      </td>
                      <td className="px-4 py-3.5">
                        <p className="font-medium text-[#1A1A2E] truncate max-w-[200px]">{inv.buyer_name || '—'}</p>
                        <p className="text-[11px] text-[#9CA3AF] truncate max-w-[200px]">{inv.buyer_email}</p>
                        {(inv.buyer_city || inv.buyer_state) && (
                          <p className="text-[10px] text-[#9CA3AF]">{[inv.buyer_city, inv.buyer_state].filter(Boolean).join(', ')}</p>
                        )}
                      </td>
                      <td className="hidden md:table-cell px-4 py-3.5 text-[#4A4A60]">
                        <p className="truncate max-w-[180px]">{inv.service_name || '—'}</p>
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3.5">
                        <span className={`inline-block text-[10px] font-body font-semibold px-2 py-0.5 rounded-full ${brandMeta.color}`}>
                          {brandMeta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-block text-[10px] font-body font-semibold px-2 py-0.5 rounded-full ${statusMeta.color}`}>
                          {statusMeta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-right whitespace-nowrap">
                        <p className="font-heading font-semibold text-[#1A1A2E]">{formatMoney(inv.total_amount, inv.currency)}</p>
                        {inv.gst_amount > 0 && (
                          <p className="text-[10px] text-[#9CA3AF]">incl. GST {formatMoney(inv.gst_amount, inv.currency)}</p>
                        )}
                      </td>
                      <td className="hidden xl:table-cell px-4 py-3.5 text-xs text-[#9CA3AF] whitespace-nowrap">
                        {inv.issued_at ? new Date(inv.issued_at).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td className="px-4 py-3.5 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1 justify-end">
                          <button
                            onClick={() => handlePreview(inv)}
                            disabled={previewingId === inv.id}
                            title="Preview invoice"
                            className="inline-flex items-center gap-1 text-xs font-body font-medium text-[#6C63FF] hover:bg-[#6C63FF]/10 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-60"
                          >
                            {previewingId === inv.id ? (
                              <span className="w-3 h-3 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Eye className="h-3.5 w-3.5" />
                            )}
                            Preview
                          </button>
                          <button
                            onClick={() => handleDownload(inv)}
                            disabled={downloadingId === inv.id}
                            title="Download invoice PDF"
                            className="inline-flex items-center gap-1 text-xs font-body font-medium text-[#6C63FF] hover:bg-[#6C63FF]/10 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-60"
                          >
                            {downloadingId === inv.id ? (
                              <span className="w-3 h-3 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Download className="h-3.5 w-3.5" />
                            )}
                            PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview modal — full-screen PDF viewer using a blob URL. */}
      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closePreview}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E8E6F8] flex-shrink-0">
              <div className="min-w-0">
                <p className="text-sm font-heading font-semibold text-[#1A1A2E] truncate">
                  {preview.invoice.invoice_number}
                </p>
                <p className="text-xs text-[#6B7280] font-body truncate">
                  {preview.invoice.buyer_name || '—'} · {formatMoney(preview.invoice.total_amount, preview.invoice.currency)}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                <button
                  onClick={() => handleDownload(preview.invoice)}
                  className="inline-flex items-center gap-1.5 text-xs font-body font-medium text-white bg-[#6C63FF] hover:bg-[#5B52CC] px-3 py-1.5 rounded-lg transition-colors"
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </button>
                <button
                  onClick={closePreview}
                  className="p-1.5 rounded-lg hover:bg-[#F5F4FB] text-[#6B7280] hover:text-[#1A1A2E] transition-colors"
                  title="Close (Esc)"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <iframe
              src={preview.url}
              title={`Invoice ${preview.invoice.invoice_number}`}
              className="flex-1 w-full bg-[#F5F4FB]"
            />
          </div>
        </div>
      )}

      {/* Pagination */}
      {data.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#6B7280] font-body">
            Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, data.total)} of {data.total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <span className="text-xs font-body text-[#6B7280]">Page {page} / {data.pages}</span>
            <button
              onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
              disabled={page === data.pages}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
